import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
} from 'chart.js'
import moment from 'moment'
import { breakdownChartData, chartBlue } from './components/tileColors'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler)

const bucketLabel = (isoDate, bucket) => {
  const date = moment(isoDate)
  if (bucket === 'month') return date.format('MMM YY')
  return date.format('D MMM')
}

// Charts are drawn at 2x the point size they occupy on the page (A4 content
// width is ~535pt) so the bitmap prints crisply.
const CANVAS_WIDTH = 1070

const renderToImage = (type, data, options, height) => {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = height
  const chart = new ChartJS(canvas.getContext('2d'), {
    type,
    data,
    options: {
      ...options,
      responsive: false,
      animation: false,
      devicePixelRatio: 1,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
    },
  })
  const src = canvas.toDataURL('image/png')
  chart.destroy()
  return { src, width: CANVAS_WIDTH / 2, height: height / 2 }
}

const renderTrend = (kpi) => {
  const isCount = kpi.unit !== 'percent' && kpi.unit !== 'minutes'
  return renderToImage(
    isCount ? 'bar' : 'line',
    {
      labels: kpi.series.map((point) => bucketLabel(point.t, kpi.bucket)),
      datasets: [
        {
          data: kpi.series.map((point) => point.v),
          ...(isCount
            ? { backgroundColor: chartBlue, borderRadius: 6, maxBarThickness: 32 }
            : {
                borderColor: chartBlue,
                borderWidth: 3,
                pointRadius: 0,
                fill: true,
                backgroundColor: 'rgba(38, 142, 187, 0.08)',
              }),
        },
      ],
    },
    {
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 8, font: { size: 20 } } },
        y: {
          beginAtZero: true,
          ticks: { maxTicksLimit: 5, precision: isCount ? 0 : undefined, font: { size: 20 } },
          max: kpi.unit === 'percent' ? 100 : undefined,
        },
      },
    },
    280,
  )
}

const renderBreakdown = (kpi) => {
  const items = kpi.items ?? []
  const chart = breakdownChartData(items)
  return renderToImage(
    'bar',
    {
      labels: chart.labels,
      datasets: [
        {
          data: chart.values,
          backgroundColor: chart.colors,
          borderRadius: 6,
          maxBarThickness: 32,
        },
      ],
    },
    {
      indexAxis: 'y',
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0, maxTicksLimit: 6, font: { size: 20 } } },
        y: { grid: { display: false }, ticks: { font: { size: 20 } } },
      },
    },
    Math.max(180, items.length * 52 + 70),
  )
}

// Returns { [kpi.key]: { src, width, height } } for every chartable KPI.
// Sizes are the point dimensions the image should occupy in the PDF.
const renderKpiChartImages = (kpis) => {
  const images = {}
  ;(kpis ?? []).forEach((kpi) => {
    if (kpi.type === 'trend' && kpi.series?.length) images[kpi.key] = renderTrend(kpi)
    if (kpi.type === 'breakdown' && kpi.items?.length) images[kpi.key] = renderBreakdown(kpi)
  })
  return images
}

export default renderKpiChartImages
